import { Link as RouterLink } from "react-router-dom";
import { Toolbar, IconButton, Divider, Drawer, Link, Typography, styled, Box } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuItems from "./MenuItems";
import { getSeriesLogoUrl } from "../../config/RaceConfigs";
import { useMobileScreen } from "../../lib/hooks";

const drawerWidth = 240;

const StyledDrawer = styled(Drawer, { shouldForwardProp: (prop) => prop !== "isMobile" })(
  ({ theme, open, isMobile }) => ({
    "& .MuiDrawer-paper": {
      whiteSpace: "nowrap",
      width: drawerWidth,
      boxSizing: "border-box",
      overflowY: "auto",
      overflowX: "visible",
      ...(!isMobile && {
        position: "relative",
        transition: theme.transitions.create("width", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        ...(!open && {
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          width: theme.spacing(7),
          [theme.breakpoints.up("sm")]: {
            width: theme.spacing(8),
          },
        }),
      }),
    },
  }),
);

const DrawerBorder = styled(Box, { shouldForwardProp: (prop) => prop !== "open" })(({ theme, open }) => ({
  position: "absolute",
  right: 0,
  top: 0,
  bottom: 0,
  width: "12px",
  cursor: "pointer",
  zIndex: 1,
  transition: theme.transitions.create(["background-color", "box-shadow"], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.short,
  }),
  "&:hover": {
    backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
    boxShadow: `inset ${open ? "-4px" : "4px"} 0 8px ${
      theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.1)"
    }`,
    "& .chevron-indicator": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
}));

const ChevronIndicator = styled(Box, { shouldForwardProp: (prop) => prop !== "open" })(({ theme }) => ({
  position: "absolute",
  bottom: theme.spacing(2),
  right: "2px",
  opacity: 0,
  transform: "translateY(8px)",
  transition: theme.transitions.create(["opacity", "transform"], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.short,
  }),
  backgroundColor: theme.palette.background.paper,
  borderRadius: "50%",
  width: 20,
  height: 20,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: theme.shadows[2],
  color: theme.palette.primary.main,
  pointerEvents: "none",
  zIndex: 2,
}));

/**
 * Left navigation drawer. Permanent on desktop (collapsible), temporary overlay on mobile.
 * @param {Object} props
 * @param {boolean} props.open - Whether the drawer is open/expanded
 * @param {() => void} props.toggleDrawer - Toggle open/close
 */
function LeftMenu({ open, toggleDrawer }) {
  const { isMobile } = useMobileScreen();

  return (
    <StyledDrawer
      variant={isMobile ? "temporary" : "permanent"}
      open={open}
      isMobile={isMobile}
      onClose={isMobile ? toggleDrawer : undefined}
      sx={{ displayPrint: "none" }}>
      {!isMobile && (
        <DrawerBorder open={open} onClick={toggleDrawer}>
          <ChevronIndicator open={open} className="chevron-indicator">
            {open ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </ChevronIndicator>
        </DrawerBorder>
      )}

      <Toolbar
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: [1],
        }}>
        <Link
          component={RouterLink}
          to={"/"}
          sx={{ textDecoration: "none", px: [1], display: "flex", alignItems: "center", gap: 3 }}>
          <img src={getSeriesLogoUrl("b7")} alt="B7 ID Aplikace" width={32} height={32} />
          <Typography color={"primary"} variant={"button"} display={"block"}>
            B7 ID Aplikace
          </Typography>
        </Link>

        <IconButton onClick={toggleDrawer}>
          <ChevronLeftIcon />
        </IconButton>
      </Toolbar>

      <Divider />

      <MenuItems menuOpen={open} />
    </StyledDrawer>
  );
}

export default LeftMenu;
